/**
 *  ・クラス名
 *    ファイル取込み
 *  ・概要
 *    ファイル取込みに必要な処理を記述
 *  ・更新履歴
 *    2021/09/21 ファイル作成 岡村
 */
import { fileActionDefautMessage } from '@/constants/form/form';
import { HexaFileFieldResponse } from '@/services/hexabase/types';
import BaseModel from '../baseModel';

export default class FileClass extends BaseModel {

  constructor() {
    super();
  }

  /**
   * ファイルアップロード時のメッセ―ジを取得します
   * @param {boolean} isStatus - 成功・失敗
   * @returns {string} - メッセージ
   */
  public getUploadMessage(isStatus: boolean) : string {
    const defaultMessage = isStatus ? fileActionDefautMessage.upload.success : fileActionDefautMessage.upload.fail;
    return defaultMessage;
  }

  /**
   * ファイル削除時のメッセ―ジを取得します
   * @param {boolean} isStatus - 成功・失敗
   * @returns {string} - メッセージ
   */
  public getDeleteMessage(isStatus: boolean) : string {
    const defaultMessage = isStatus ? fileActionDefautMessage.delete.success : fileActionDefautMessage.delete.fail;
    return defaultMessage;
  }

  /**
   * ファイルダウンロード時のメッセ―ジを取得します
   * @param {boolean} isStatus - 成功・失敗
   * @returns {string} - メッセージ
   */
  public getDownloadMessage(isStatus: boolean) : string {
    const defaultMessage = isStatus ? fileActionDefautMessage.download.success : fileActionDefautMessage.download.fail;
    return defaultMessage;
  }

  /**
   * アイテムのファイルリスト取得
   * @param {string} appId - アプリID
   * @param {string} dsId - データストアID
   * @param {string} itemId - アイテムID
   * @returns {{ rev: number, files:HexaFileFieldResponse[] }}
   */
  public async getFileList (appId: string, dsId: string, itemId: string): Promise<{ rev: number, files:HexaFileFieldResponse[] }>{
    const result = await this.getItemDetails(appId, dsId, itemId,'map');
    const revNo = result.rev_no;
    if (result.field_values && Array.isArray(result.field_values['files'].value)) {
      return {
        rev: revNo,
        files: result.field_values['files'].value as HexaFileFieldResponse[]
      };
    }
    return { rev: revNo, files:[] };
  }

  /**
   * 対象ファイルをアップロード
   * @param {File[]} inputFiles - アップ対象ファイル
   * @returns 
   */
  public async uploadFiles(
    inputFiles: File[]
  ) {
    const upfile: string[] = []
    for (const inputFile of inputFiles) {
      const formData = new FormData();
      formData.append('file', inputFile);
      formData.append('filename', inputFile.name);
      const result = await this.uploadFile(formData);
      upfile.push(result.data.file_id);
    }
    return upfile;
  }

  /**
   * アクション実行
   * @param {string} appId - アプリID
   * @param {string} dsId - データストアID
   * @param {string} itemId - アイテムID
   * @param {string} actionId - アクションID
   * @param {number} revNo - リビジョン番号
   * @param {string} files - ファイル
   */
  public async execAction(
    appId: string,
    dsId: string,
    itemId: string,
    actionId: string,
    revNo: number,
    files: string[]
  ) {
    const params = {
      'rev_no': revNo,
      item: {
        'files':files
      }
    }

    const result = await this.changeStatus(
        appId,
        dsId,
        itemId,
        actionId,
        params
      );
  }

  /**
   * ファイルインプットルールの付与
   * @param {HexaFileFieldResponse[]} currentFiles 
   * @param {string} rules 
   * @returns {string}
   */
  public modifyRule(currentFiles: HexaFileFieldResponse[], rules?: string): string{

    if (rules === undefined || rules === null) {
      return '';
    }

    if (rules.length > 0) {
      const splitRule = rules.split('|');
      return splitRule.map(v => {
        if (v.includes('fileMaxCount')) {
          const index = v.indexOf(':');
          if (index === -1) return v;
          const masterCount = Number(v.substring(index).replace(':', ''));
          if (!Number.isInteger(masterCount)) return v;
          return [v.substring(0,index), masterCount - currentFiles.length].join(':');
        }
        if (v.includes('fileRequired')) {
          return undefined;
        }
        return v;
      }).filter(v => v).join('|')
    }
    return '';
  }

  /**
   * ファイルインプットルールの付与
   * @param {HexaFileFieldResponse[]} currentFiles 
   * @param {string} rules 
   * @returns {string}
   */
  public getCountRule(currentFiles: HexaFileFieldResponse[], rules?: string): string{

    if (rules === undefined || rules === null) {
      return '';
    }
    if (rules.length > 0) {
      const splitRule = rules.split('|');
      return splitRule.map(v => {
        if (v.includes('fileRequired')) {
          return `${v}:${currentFiles.length}`;
        }
        return undefined;
      }).filter(v => v).join('|')
    }
    return '';
  }

  /**
   * ファイルのダウンロード
   * @param {{[k:string]: string}[]} fileList
   * @returns {void}
   */
  public async downLoadFiles(fileList: {[k:string]: string}[]): Promise<void> {
    for (const index in fileList) {
      const result = await this.getFile(fileList[index].file_id);
      this.formOutPutDownload(result, undefined, undefined, fileList[index]);
    }
  }

  /**
   * 指定ファイルの削除
   * @param {string} appId - アプリID
   * @param {string} dsId - データストアID
   * @param {string} itemId - アイテムID
   * @param {string} actionId - アクションID
   * @param {number} revNo - リビジョン番号
   * @param {{[k:string]: string}[]} selectItems - 削除対象ファイル情報
   * @param {HexaFileFieldResponse[]} currentFiles - 今現在アップロードされているファイル
   * @returns {void}
   */
  public async deleteFiles(
    appId: string,
    dsId: string,
    itemId: string,
    actionId: string,
    revNo: number,
    selectItems: {[k:string]: string}[],
    currentFiles: HexaFileFieldResponse[]
    ): Promise<void> {
      for (const index in selectItems) {
        await this.deleteFile(selectItems[index].file_id);
      }
      const del = selectItems.map(v => v.file_id);
      const current = currentFiles.map(v => v.file_id);
      await this.execAction(appId, dsId, itemId, actionId, revNo, current.filter(i => del.indexOf(i) == -1));
  }

}
